import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useScript } from "../hooks/useScript";
import { SanityInputTextProps } from "../models/form/SanityFormInputField";
import { FormValues, SanityFormProps } from "../models/form/SanityFormProps";
import { SanityInputFieldType } from "../models/form/SanityInputFieldType";
import SanityForm from "./Form/SanityForm";

export interface HouseHealthFormProps {
  contactForm?: SanityFormProps;
  form?: SanityFormProps;
}

type QuestionAndAnswer = {
  answer: string;
  question: string;
};

type CustomEventDetail = {
  simpleQuestionAnswers: QuestionAndAnswer[];
};

declare const initiateQuiz: () => void;

const QUESTIONS_AND_ANSWERS_FIELD_NAME = "__houseHealthQA";
const SUMMARY_FIELD_NAME = "__houseHealthSummary";

export const HouseHealthForm = ({ contactForm, form }: HouseHealthFormProps) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<QuestionAndAnswer[]>([]);
  const [summaryHtml, setSummaryHtml] = useState<string>();

  useScript({
    src: "/houseHealthForm/script.v3.js",
    onLoad: useCallback(() => {
      setScriptLoaded(true);
    }, [])
  });

  useEffect(() => {
    if (scriptLoaded) {
      const callback = (event: CustomEvent<CustomEventDetail>) => {
        setQuestionsAndAnswers(event.detail.simpleQuestionAnswers);

        const summaryElement = document.querySelector("#hh-quiz__summary");

        if (summaryElement) {
          // Clone the summary element so we can modify it for emails without affecting the DOM.
          const clone = summaryElement.cloneNode(true) as Element;

          const headingElement = clone.querySelector("#hh-conclusion-heading");
          const subheadingElement = clone.querySelector("#hh-conclusion-subheading");

          if (headingElement && subheadingElement) {
            // Apply different styling for the summary content's heading inside emails:
            // * Increase the font size of the h3 heading element.
            // * Put the heading element together with the subheading element on the same row, separated by a dash .
            // * Remove the old subheading element.
            headingElement.innerHTML = `<span style="font-size: 1.5em;">${headingElement.innerHTML} - ${subheadingElement.innerHTML}</span>`;
            subheadingElement.remove();
          }

          setSummaryHtml(clone.innerHTML);
        }
      };

      // Listen to a custom event fired by the static script file
      window.addEventListener("houseHealthFormShowGetResultsForm", callback);

      // Note: We call initiateQuiz() from the houseHealthForm script.js file here, to ensure the init
      // function runs if this component re-mounts (as it does in eg preview sites).
      initiateQuiz();

      return () => {
        window.removeEventListener("houseHealthFormShowGetResultsForm", callback);
      };
    }
  }, [scriptLoaded]);

  const questionsAndAnswersField: SanityInputTextProps = useMemo(
    () => ({
      _type: SanityInputFieldType.Text,
      hidden: true,
      label: "Questions and answers",
      readonly: true,
      slug: { current: QUESTIONS_AND_ANSWERS_FIELD_NAME }
    }),
    []
  );

  const summaryField: SanityInputTextProps = useMemo(
    () => ({
      _type: SanityInputFieldType.Text,
      hidden: true,
      label: "Summary",
      readonly: true,
      slug: { current: SUMMARY_FIELD_NAME }
    }),
    []
  );

  // Add extra hidden fields to both forms
  const modifiedFormInputs = useMemo(() => {
    const originalFormInputs = form?.inputs || form?._rawInputs;

    return originalFormInputs
      ? [...originalFormInputs, questionsAndAnswersField, summaryField]
      : undefined;
  }, [form?._rawInputs, form?.inputs, questionsAndAnswersField, summaryField]);

  const modifiedContactFormInputs = useMemo(() => {
    const originalContactFormInputs = contactForm?.inputs || contactForm?._rawInputs;

    return originalContactFormInputs
      ? [...originalContactFormInputs, questionsAndAnswersField, summaryField]
      : undefined;
  }, [
    contactForm?._rawInputs,
    contactForm?.inputs,
    questionsAndAnswersField,
    summaryField
  ]);

  // Callback for updating the extra hidden fields
  const onModifyValue = useCallback(
    (): FormValues => ({
      [QUESTIONS_AND_ANSWERS_FIELD_NAME]: JSON.stringify(questionsAndAnswers),
      [SUMMARY_FIELD_NAME]: summaryHtml
    }),
    [questionsAndAnswers, summaryHtml]
  );

  // Notify the house health script.js file with another event
  const onSubmitGetResultsForm = useCallback(() => {
    const notifyScriptFileEvent = new CustomEvent("houseHealthFormShowSummary");

    window.dispatchEvent(notifyScriptFileEvent);
  }, []);

  return (
    <div className="w-full max-w-screen-xl mx-auto mb-12 px-4 md:px-6 lg:mb-32">
      <Helmet>
        <link rel="stylesheet" href="/houseHealthForm/styles.min.css" />
      </Helmet>

      <section id="hh-quiz">
        <h2>Hälsotest</h2>
        <div id="hh-quiz__progress"></div>

        <form id="hh-quiz__form" action=""></form>

        <div
          className="hh-quiz-hidden w-full max-w-[560px]"
          id="hh-quiz__get-results-form"
        >
          {form && (
            <SanityForm
              {...form}
              inputs={modifiedFormInputs}
              modifyValue={onModifyValue}
              onSubmitted={onSubmitGetResultsForm}
            />
          )}
        </div>

        <div id="hh-quiz__step-summary" className="hh-quiz__summary-hidden">
          <div id="hh-quiz__summary"></div>

          <p className="hh-quiz__summary-extra">
            <em>
              Resultatet från Hushälsotestet ger enbart riktlinjer kring hälsan på ditt
              hus. Testet tar ej heller hänsyn till installationer, våtutrymmen eller
              vitvaror. För en säker diagnos, boka en husbesiktning eller teckna vår
              Anticimex Hushälsoförsäkring för ett kompletterande skydd utöver din vanliga
              husförsäkring.
            </em>
          </p>

          <button id="hh-quiz__contact" type="button" className="hh-quiz__button">
            Bli kontaktad
          </button>

          <div
            className="w-full max-w-[560px] p-10 my-10 bg-neutral-100 border border-brand-primary-90 rounded-2xl hh-quiz-hidden"
            id="hh-quiz__get-contacted"
          >
            {contactForm && (
              <SanityForm
                {...contactForm}
                inputs={modifiedContactFormInputs}
                modifyValue={onModifyValue}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
